import { useEffect, useState } from 'react';

import { Box, useTheme } from '@mui/material';
import DashboardPlugin from '@uppy/dashboard';
import ScreenCapture from '@uppy/screen-capture';
import Webcam from '@uppy/webcam';
import type { BasePlugin } from '@uppy/core';
import type Uppy from '@uppy/core';

import { useUiContext } from '../../../../contexts/UiContext/hooks/useUiContext.hook';

const DASHBOARD_PLUGIN_ID = 'Dashboard';

interface FilesUploadDashboardProps {
  readonly uppy: Uppy;
}

export const FilesUploadDashboard = ({
  uppy,
}: Readonly<FilesUploadDashboardProps>) => {
  const muiTheme = useTheme();
  // A callback ref (rather than a ref object) so the install effect below re-runs once the
  // DOM node is actually available, instead of assuming it already is on the first pass.
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const { showDataGrid, hideDataGrid } = useUiContext();

  // Reuses the DataGrid viewport-bounding mechanism: it's what makes MainContent switch to a
  // fixed-height layout (instead of growing the page) so this panel can fill 100% and let Uppy's
  // own dashboard scroll internally, same as a DataGrid does.
  useEffect(() => {
    const token = showDataGrid();

    return () => {
      hideDataGrid(token);
    };
  }, [showDataGrid, hideDataGrid]);

  // @uppy/react's <Dashboard> wrapper installs the Dashboard plugin asynchronously from its
  // own componentDidMount, which races with Webcam/ScreenCapture's `target: Dashboard` lookup
  // (it can run before Dashboard exists, leaving the Dashboard plugin re-created empty when it
  // finally mounts). Installing all three ourselves, in one synchronous chain, avoids that race.
  useEffect(() => {
    if (!container) {
      return undefined;
    }

    // The cast works around a generics-variance quirk in @uppy/core's PluginTarget type
    // when referencing another plugin's generic class directly.
    const dashboardTarget = DashboardPlugin as unknown as typeof BasePlugin;

    uppy
      .use(DashboardPlugin, {
        id: DASHBOARD_PLUGIN_ID,
        inline: true,
        target: container,
        proudlyDisplayPoweredByUppy: false,
        theme: muiTheme.palette.mode,
      })
      .use(Webcam, { target: dashboardTarget })
      .use(ScreenCapture, { target: dashboardTarget });

    return () => {
      ['Webcam', 'ScreenCapture', DASHBOARD_PLUGIN_ID].forEach((id) => {
        const plugin = uppy.getPlugin(id);
        if (plugin) {
          uppy.removePlugin(plugin);
        }
      });
    };
    // theme is intentionally excluded: switching it is handled below without remounting Dashboard
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uppy, container]);

  // Reflects MUI's light/dark mode onto an already-mounted Dashboard without remounting it
  useEffect(() => {
    uppy
      .getPlugin(DASHBOARD_PLUGIN_ID)
      ?.setOptions({ theme: muiTheme.palette.mode });
  }, [uppy, muiTheme.palette.mode]);

  return (
    <Box
      sx={{
        justifyItems: 'center',
        display: 'flex',
        flexGrow: 1,
        alignItems: 'center',
        overflow: 'hidden',
        maxWidth: '100%',
        '& .uppy-Dashboard': {
          maxWidth: '100%',
        },
        '& .uppy-Root': {
          maxWidth: '100%',
        },
      }}
    >
      <div
        className="uppy-Container"
        ref={setContainer}
        style={{
          display: 'flex',
          maxWidth: '100%',
        }}
      />
    </Box>
  );
};
